/**
 * Creates a profanity filter that checks for exact word matches.
 * Uses Unicode-aware boundaries (\p{L}) so accented characters in
 * Spanish, Portuguese, and other languages are handled correctly
 * (e.g. "cuál" won't match "culea", "pão" won't match "são").
 *
 * Also normalizes common leet-speak substitutions and repeated characters
 * to catch basic evasion attempts.
 */
export function createProfanityFilter(wordList) {
  const escapedWords = wordList.map((word) => word.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'))
  // Use Unicode property escapes for word boundaries instead of \b,
  // which only recognizes [a-zA-Z0-9_] and fails with accented chars.
  const pattern = `(?<!\\p{L})(${escapedWords.join('|')})(?!\\p{L})`
  const regex = new RegExp(pattern, 'iu')

  return {
    isProfane: (text) => {
      // Check original text
      if (regex.test(text)) return true
      // Normalize common leet-speak and check again
      const normalized = normalizeLeetSpeak(text)
      if (normalized !== text && regex.test(normalized)) return true
      // Collapse repeated characters (e.g. "puuutaaa" -> "puta")
      const collapsed = collapseRepeats(normalized)
      if (collapsed !== normalized && regex.test(collapsed)) return true
      return false
    },
  }
}

/**
 * Replaces common leet-speak character substitutions.
 */
function normalizeLeetSpeak(text) {
  return text
    .replace(/0/g, 'o')
    .replace(/1/g, 'i')
    .replace(/3/g, 'e')
    .replace(/4/g, 'a')
    .replace(/5/g, 's')
    .replace(/7/g, 't')
    .replace(/@/g, 'a')
    .replace(/\$/g, 's')
}

/**
 * Collapses sequences of 3+ repeated characters to a single one.
 * E.g. "puuutaaa" -> "puta", but "leer" stays as "leer".
 */
function collapseRepeats(text) {
  return text.replace(/(.)\1{2,}/g, '$1')
}
